import React from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import UrgencyBanner from '../components/UrgencyBanner';
import { contactInfo } from '../data/mock';
import { Phone, MessageCircle, Heart, Clock, Shield, Star, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';

const RetourAffectif = () => {
  return (
    <div className="bg-black min-h-screen">
      <SEOHead 
        title="Retour Affectif Rapide et Puissant | Grand Marabout Africain | Résultats 24-72h"
        description="Retour affectif rapide et efficace par un grand marabout africain. Spécialiste du retour affectif depuis 30 ans. Faites revenir l'être aimé en 24-72h. Magie blanche, noire, rouge. +290 clients satisfaits."
        keywords="retour affectif, retour affectif rapide, retour affectif puissant, retour affectif efficace, retour affectif garanti, retour affectif marabout, retour de l'être aimé, rituel retour affectif, magie retour affectif"
        canonical="https://retour-affectif-rapide.com/retour-affectif"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center justify-center py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
          
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <span className="inline-block px-6 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-500 text-sm font-semibold tracking-wide mb-8">
              N°1 du Retour Affectif
            </span>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              <span className="text-amber-500">Retour Affectif</span>
              <br />
              <span className="text-3xl sm:text-4xl md:text-5xl">Rapide et Puissant</span>
            </h1>
            
            <p className="text-gray-300 text-lg sm:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
              Le <strong>retour affectif</strong> est notre spécialité depuis plus de 30 ans. 
              <strong className="text-amber-500"> Maître Diamma</strong>, grand marabout africain, 
              vous aide à retrouver l'amour grâce à des rituels ancestraux puissants.
              <strong className="text-amber-500"> Résultats garantis en 24-72h</strong>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href={contactInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold text-lg rounded-full transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-6 h-6 mr-3" />
                Consultation Retour Affectif
              </a>
              <a
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-amber-500 text-amber-500 font-bold text-lg rounded-full hover:bg-amber-500 hover:text-black transition-all duration-300"
              >
                <Phone className="w-6 h-6 mr-3" />
                {contactInfo.phone}
              </a>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <span className="flex items-center"><Sparkles className="w-4 h-4 mr-2 text-amber-500" />30 ans d'expérience</span>
              <span className="flex items-center"><Clock className="w-4 h-4 mr-2 text-amber-500" />Résultats rapides</span>
              <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-green-500" />+290 clients satisfaits</span>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900/50 border border-amber-500/20 rounded-2xl p-8 sm:p-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
                Qu'est-ce que le <span className="text-amber-500">Retour Affectif</span> ?
              </h2>
              
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  Le <strong className="text-white">retour affectif</strong> est un ensemble de rituels spirituels ancestraux 
                  visant à faire revenir une personne aimée ou à restaurer l'harmonie dans une relation amoureuse. 
                  Ces pratiques, transmises de génération en génération dans les traditions africaines, 
                  sont reconnues pour leur <strong className="text-amber-500">efficacité exceptionnelle</strong>.
                </p>
                
                <h3 className="text-2xl font-bold text-white mt-8">Les différents types de retour affectif</h3>
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                  <div className="bg-black/50 p-4 rounded-xl border border-white/20 text-center">
                    <h4 className="font-bold text-white mb-2">Magie Blanche</h4>
                    <p className="text-sm">Énergies positives pour attirer l'amour et la protection</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-xl border border-gray-700 text-center">
                    <h4 className="font-bold text-white mb-2">Magie Noire</h4>
                    <p className="text-sm">Puissante pour les cas complexes nécessitant une action rapide</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-xl border border-red-900/50 text-center">
                    <h4 className="font-bold text-red-400 mb-2">Magie Rouge</h4>
                    <p className="text-sm">Dédiée à l'amour, la passion et le désir</p>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mt-8">Comment fonctionne le retour affectif ?</h3>
                <p>
                  Le <strong>retour affectif</strong> agit sur les énergies spirituelles qui lient deux personnes. 
                  Même après une séparation, ces liens persistent. Nos rituels permettent de :
                </p>
                <ul className="list-disc list-inside space-y-3 ml-4">
                  <li>Raviver les sentiments de l'être aimé</li>
                  <li>Lever les blocages émotionnels</li>
                  <li>Renforcer les liens affectifs</li>
                  <li>Protéger le couple contre les influences négatives</li>
                </ul>
                
                <h3 className="text-2xl font-bold text-white mt-8">Pourquoi choisir notre retour affectif ?</h3>
                <ul className="list-disc list-inside space-y-3 ml-4">
                  <li><strong>30 ans d'expérience</strong> dans les arts mystiques africains</li>
                  <li><strong>+290 clients satisfaits</strong> à travers le monde</li>
                  <li><strong>Résultats en 24-72h</strong> dans la plupart des cas</li>
                  <li><strong>Confidentialité absolue</strong> garantie</li>
                  <li><strong>Suivi personnalisé</strong> jusqu'à l'obtention des résultats</li>
                </ul>
                
                <div className="grid gap-4 mt-8">
                  <div className="bg-black/50 p-4 rounded-xl border border-amber-500/10">
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                    </div>
                    <p className="italic">"Le retour affectif a changé ma vie. Mon ex est revenu en 3 jours !"</p>
                    <p className="text-amber-500 mt-2 text-sm">- Julie, Montréal</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-xl border border-amber-500/10">
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                    </div>
                    <p className="italic">"Après des mois de souffrance, le retour affectif m'a rendu le bonheur."</p>
                    <p className="text-amber-500 mt-2 text-sm">- Nicolas, Marseille</p>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <div className="mt-12 p-6 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center">
                <p className="text-white font-bold text-xl mb-4">
                  Besoin d'un retour affectif ?
                </p>
                <p className="text-gray-300 mb-6">
                  Consultation gratuite et confidentielle
                </p>
                <a
                  href={contactInfo.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 hover:bg-amber-400 text-black font-bold text-lg rounded-full transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-3" />
                  Demander un retour affectif
                  <ArrowRight className="w-5 h-5 ml-3" />
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 text-center">
              Questions sur le <span className="text-amber-500">Retour Affectif</span>
            </h2>
            
            <div className="space-y-6">
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">Le retour affectif est-il efficace ?</h3>
                <p className="text-gray-300">Oui, le retour affectif pratiqué par un vrai marabout africain est très efficace. Avec plus de 30 ans d'expérience et des centaines de témoignages positifs, notre taux de réussite est de 98%.</p>
              </div>
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">Le retour affectif fonctionne-t-il à distance ?</h3>
                <p className="text-gray-300">Absolument. L'énergie spirituelle ne connaît pas de frontières. Nous réalisons des retours affectifs efficaces pour des clients du monde entier : France, Belgique, Suisse, Canada, Afrique...</p>
              </div>
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">Combien coûte un retour affectif ?</h3>
                <p className="text-gray-300">Le tarif dépend de la complexité de votre situation. La première consultation est gratuite et nous vous donnons un devis personnalisé après analyse de votre cas.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <FloatingWhatsApp />
      <UrgencyBanner />
    </div>
  );
};

export default RetourAffectif;
